import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TipoVariavelService } from 'src/services/tipo-variavel.service';
import { PermissoesService } from 'src/services/permissoes.service';
import { MessageService, ConfirmationService } from 'primeng/api';

@Component({
    selector: 'app-tipo-sensor',
    templateUrl: './tipo-sensor.component.html',
    styleUrls: ['./tipo-sensor.component.scss'],
    standalone: false
})
export class TipoSensorComponent implements OnInit, AfterViewInit {
  data: any[] = [];
  form: FormGroup;
  displayModal: boolean = false;
  loading: boolean = false;
  direitoInserir: boolean = false;
  direitoEditar: boolean = false;
  direitoExcluir: boolean = false;
  dropDownTipoGrafico: any[] = ['line', 'bar'];
  @ViewChild('tableWrapper', { static: false }) tableWrapper: ElementRef;
  scrollHeight: string = '600px';

  constructor(
    protected _tipoVariavelService: TipoVariavelService,
    protected _formBuilder: FormBuilder,
    protected permissaoService: PermissoesService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.direitoInserir = this.permissaoService.can('sensores_inserir');
    this.direitoEditar = this.permissaoService.can('sensores_editar');
    this.direitoExcluir = this.permissaoService.can('sensores_excluir');

    this.form = this._formBuilder.group({
      id: [null],
      tipo_variavel: [null, Validators.required],
      tipo_grafico: [null, Validators.required],
      ordem: [null, Validators.required],
      status: [true],
    })

    this.index()
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.calculateScrollHeight();
    }, 200);
  }

  calculateScrollHeight(): void {
    if (this.tableWrapper && this.tableWrapper.nativeElement) {
      const wrapperHeight = this.tableWrapper.nativeElement.offsetHeight;
      const calculatedHeight = wrapperHeight - 120;
      this.scrollHeight = `${Math.max(calculatedHeight, 300)}px`;
    }
  }

  index(){
    this._tipoVariavelService.indexSensores().subscribe({
      next: (json) => {
        // A API retorna um array direto
        let dados = json;
        
        // Verifica se a resposta tem estrutura data ou é array direto
        if (json && json.data && Array.isArray(json.data)) {
          dados = json.data;
        } else if (!Array.isArray(json)) {
          dados = [];
        }
        
        // Mostra todos os registros (tipo_variavel é apenas a descrição)
        this.data = dados || [];
        this.cdr.detectChanges();
        setTimeout(() => {
          this.calculateScrollHeight();
        }, 200);
      },
      error: (err) => {
        console.error('Erro ao carregar sensores:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao carregar sensores'
        });
      }
    });
  }

  openNew(){
    this.form.reset();
    this.form.patchValue({
      status: true
    });
    this.displayModal = true;
  }

  edit(event){
    this._tipoVariavelService.show(event.id).subscribe({
      next: (json) => {
        if(json.status == 0){
          this.form.get('status').setValue(true);
        } else {
          this.form.get('status').setValue(false);
        }
        this.form.patchValue(json);
        this.displayModal = true;
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao carregar sensor'
        });
      }
    });
  }

  save(){
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Preencha todos os campos obrigatórios'
      });
      return;
    }

    this.loading = true;
    let form = this.form.value;
    let params = {
      tipo_variavel: form.tipo_variavel,
      tipo_grafico: form.tipo_grafico,
      ordem: form.ordem,
      status: form.status ? 0 : 1, // 0 = ativo, 1 = inativo
    };

    if(form.id == null){
      this._tipoVariavelService.store(params).subscribe({
        next: (json) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Sensor criado com sucesso'
          });
          this.index();
          this.loading = false;
          this.displayModal = false;
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Erro ao criar sensor'
          });
          this.loading = false;
        }
      });
    } else {
      this._tipoVariavelService.update(params, form.id).subscribe({
        next: (json) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Sensor atualizado com sucesso'
          });
          this.index();
          this.loading = false;
          this.displayModal = false;
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Erro ao atualizar sensor'
          });
          this.loading = false;
        }
      });
    }
  }

  /**
   * Confirmação Sim/Não antes de excluir. Chama DELETE /api/variavel/tipo/{id}.
   * Documentação: 204 sucesso; 404 tipo não encontrado; 422 existem dados coletados em estações (message + estacoes).
   */
  confirmarExclusao(sensor: any): void {
    const nome = sensor.tipo_variavel || `ID ${sensor.id}`;
    this.confirmationService.confirm({
      key: 'globalConfirm',
      message: `Deseja realmente excluir o sensor "${nome}" (código ${sensor.id})?`,
      header: 'Confirmar exclusão',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim, excluir',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.excluirSensor(sensor.id),
    });
  }

  private excluirSensor(id: number): void {
    this._tipoVariavelService.destroy(id).subscribe({
      next: () => {
        this.data = this.data.filter((item: any) => item.id !== id);
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Sensor excluído com sucesso.',
        });
        this.cdr.detectChanges();
        setTimeout(() => this.calculateScrollHeight(), 200);
      },
      error: (err) => {
        const status = err?.status;
        const body = err?.error || {};
        const msg = body?.message || body?.error;
        if (status === 404) {
          this.data = this.data.filter((item: any) => item.id !== id);
          this.messageService.add({
            severity: 'warn',
            summary: 'Atenção',
            detail: msg || 'Tipo de variável não encontrado. Removido da lista.',
          });
        } else if (status === 422) {
          const estacoes = body?.estacoes || [];
          const listaEstacoes = estacoes.length > 0
            ? estacoes.map((e: any) => e.nome || e.descricao || `ID ${e.id}`).join(', ')
            : '';
          const detail = msg
            ? (listaEstacoes ? `${msg} Estação(ões): ${listaEstacoes}` : msg)
            : (listaEstacoes
              ? `Existem dados coletados para este Sensor e Estação(ões): ${listaEstacoes}. Não é possível excluir.`
              : 'Existem dados coletados para este Sensor em estação(ões). Não é possível excluir.');
          this.messageService.add({
            severity: 'warn',
            summary: 'Não foi possível excluir',
            detail,
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: msg || 'Não foi possível excluir o sensor.',
          });
        }
      },
    });
  }
}

